/*******************************************************
 * Copyright (C) BoostCommerce.net
 *
 * This file is part of commercial BoostCommerce.net projects.
 *
 * This file can not be copied and/or distributed without the express
 * permission of BoostCommerce.net
 *
 * @Date:   2020-04-02 07:38:11
 *
 *******************************************************/
// @flow

import _ from 'lodash';
import React, { Component } from 'react';
import { Button } from '@shopify/polaris';
import { PlusMinor } from '@shopify/polaris-icons';
import { withI18n, WithI18nProps } from '@shopify/react-i18n';
import { DragDropContext } from 'react-beautiful-dnd';

import { RenderLabelTooltip } from 'src/components/views/common/forms/render-label-tooltip';
import TreeNodeCollections from './tree-node-collections';

type Props = {
  items: any[]
};

type ComposedProps = Props & WithI18nProps;

class TreeCollections extends Component<ComposedProps, State> {
  reorder = (list, startIndex, endIndex) => {
    const result = Array.from(list);
    const [removed] = result.splice(startIndex, 1);
    result.splice(endIndex, 0, removed);

    return result;
  };

  onDragEnd = result => {
    const { formValues } = this.props;
    // dropped outside the list
    if (!result.destination) {
      return;
    }

    // Get parent index
    const matches = result.draggableId.match(/\d+/g);

    if (!matches) return;

    let updatedList = _.cloneDeep(formValues);
    const indexLevel1 = matches.length >= 2 ? matches[0] : -1;
    const indexLevel2 = matches.length === 3 ? matches[1] : -1;

    if (indexLevel1 === -1 && indexLevel2 === -1) {
      updatedList = this.reorder(updatedList, result.source.index, result.destination.index);
    }

    // Drag tag at level 2
    if (indexLevel1 !== -1 && indexLevel2 === -1) {
      const tags = this.reorder(
        updatedList[indexLevel1].tags,
        result.source.index,
        result.destination.index
      );

      updatedList[indexLevel1].tags = tags;
    }

    if (indexLevel1 !== -1 && indexLevel2 !== -1) {
      const subTags = this.reorder(
        updatedList[indexLevel1].tags[indexLevel2].subTags,
        result.source.index,
        result.destination.index
      );

      updatedList[indexLevel1].tags[indexLevel2].subTags = subTags;
    }

    this.props.updateManualValues(updatedList);
  };

  render() {
    const { label, tooltip, collectionOptions, tagOptions, formValues, i18n } = this.props;

    return (
      <div>
        {/* Label and tooltip */}
        <RenderLabelTooltip label={label} tooltip={tooltip} />

        {/* List of tags */}
        <DragDropContext onDragEnd={this.onDragEnd}>
          <TreeNodeCollections
            droppableId={`treeCollections`}
            type={`treeCollections`}
            collections={formValues}
            formValues={formValues}
            collectionOptions={collectionOptions}
            tagOptions={tagOptions}
            mainIndex={0}
            level={1}
            routePath={''}
            className={''}
            addNewCollection={this.addNewCollection}
            removeCollection={this.removeCollection}
            addNewTag={this.addNewTag}
            removeTag={this.removeTag}
            showHideSubTags={this.showHideSubTags}
            updateManualValues={this.props.updateManualValues}
          />

          <div className="MultiLevelTag_ButtonAdd">
            <Button outline onClick={() => this.addNewCollection('')} icon={PlusMinor}>
              {i18n.translate(
                'AdminHTML.FilterTree.Item.optionModal_GeneralTab_Collection_TreeCollections_ButtonChildren'
              )}
            </Button>
          </div>
        </DragDropContext>
      </div>
    );
  }

  /**
   * Add new tag
   * @param  {string} pathToParent route path to selected parent tag
   */
  addNewCollection = pathToParent => {
    const { formValues } = this.props;
    const parsed = pathToParent.split('/');

    // Cannot parse route path
    if (!_.isArray(parsed)) return;

    // Get tags
    let updatedList = _.cloneDeep(formValues);

    // Add tag at level 1
    if (parsed.length === 1 && _.isEmpty(parsed[0])) {
      updatedList.push({
        label: '',
        displayName: '',
        tags: [
          {
            tag: '',
            displayName: ''
          }
        ],
        expanded: true
      });

      // Save updated values
      this.props.updateManualValues(updatedList);
      return;
    }

    // Save updated values
    this.props.updateManualValues(updatedList);
  };

  /**
   * Remove tag
   * @param  {string} pathToTag path to selected tag
   */
  removeCollection = pathToTag => {
    const { formValues } = this.props;
    const parsed = pathToTag.split('/');

    let updatedList = _.cloneDeep(formValues);

    // Remove tags at first level
    if (parsed.length === 1) {
      updatedList.splice(parsed[0], 1);
    }

    // If remove all, insert new empty collection
    if (updatedList.length === 0) {
      updatedList.push({
        handle: '',
        value: '',
        label: '',
        displayName: '',
        tags: [
          {
            tag: '',
            displayName: ''
          }
        ],
        expanded: true // Expand automatically list of tag
      });
    }

    // Save updated values
    this.props.updateManualValues(updatedList);
  };

  /**
   * Add new tag
   * @param  {string} pathToParent route path to selected parent tag
   */
  addNewTag = pathToParent => {
    const { formValues } = this.props;
    const parsed = pathToParent.split('/');
    // Cannot parse route path
    if (!_.isArray(parsed)) return;

    // Get tags
    let updatedList = _.cloneDeep(formValues);

    if (parsed.length === 1) {
      const list = updatedList[parsed[0]];
      if (list.tags) {
        updatedList[parsed[0]].tags.push({ tag: '', displayName: '' });
      } else {
        updatedList[parsed[0]].tags = [{ tag: '', displayName: '' }];
      }
      // Expand node to show sub tags
      updatedList[parsed[0]].expanded = true;
    } else {
      const list = _.get(updatedList, `[${parsed[0]}].tags[${parsed[1]}]`);
      if (list.subTags) {
        updatedList[parsed[0]].tags[parsed[1]].subTags.push({ tag: '', displayName: '' });
      } else {
        updatedList[parsed[0]].tags[parsed[1]].subTags = [{ tag: '', displayName: '' }];
      }
      // Expand node to show sub tags
      updatedList[parsed[0]].tags[parsed[1]].expanded = true;
    }

    // Save updated values
    this.props.updateManualValues(updatedList);
  };

  /**
   * Remove tag
   * @param  {string} pathToTag path to selected tag
   */
  removeTag = pathToTag => {
    const { formValues } = this.props;
    const parsed = pathToTag.split('/');

    let updatedList = _.cloneDeep(formValues);

    // Remove collection at first level
    if (parsed.length === 1) {
      updatedList.splice(parsed[0], 1);
    }

    // Remove tags at second level
    if (parsed.length === 2) {
      const list = updatedList[parsed[0]];
      if (list.tags) {
        updatedList[parsed[0]].tags.splice(parsed[1], 1);
      }
    }

    // Remove tags at third level
    if (parsed.length === 3) {
      const list = updatedList[parsed[0]].tags[parsed[1]];
      if (list.subTags) {
        updatedList[parsed[0]].tags[parsed[1]].subTags.splice(parsed[2], 1);
      }
    }

    // Save updated values
    this.props.updateManualValues(updatedList);
  };

  /**
   * Show/Hide sub tags
   * @param  {string} pathToTag path to selected tag
   */
  showHideSubTags = pathToTag => {
    const { formValues } = this.props;
    const parsed = pathToTag.split('/');

    // Get tags
    let updatedList = _.cloneDeep(formValues);
    if (parsed.length === 1) {
      const expanded = _.get(updatedList, `[${parsed[0]}].expanded`, false);
      updatedList[parsed[0]].expanded = !expanded;
    } else if (parsed.length === 2) {
      const expanded = _.get(updatedList, `[${parsed[0]}].tags[${parsed[1]}].expanded`, false);
      updatedList[parsed[0]].tags[parsed[1]].expanded = !expanded;
    }

    // Save updated values
    this.props.updateManualValues(updatedList);
  };
}
export default withI18n()(TreeCollections);
